import Box from "@mui/material/Box";

import type { ValidColor } from "../theme";
import { useColor } from "../theme";

/**
 * The brand's typographic accent: a full stop rendered as **half a dot**, set in Magic
 * Violet after a headline — `STABLE ENERGY IS NOT STIMULATION. IT IS RHYTHM.`
 *
 * Rendered as a shape rather than a `.` character so it always matches the logo, and
 * marked `aria-hidden` because a screen reader announcing "image" mid-sentence is
 * worse than the sentence simply ending.
 *
 * Prefer {@link WithHalfdot} over placing this straight after text — on its own the
 * mark can wrap onto a line of its own.
 */
export function Halfdot({
  size = 10,
  color = "accentText",
}: {
  size?: number;
  color?: ValidColor;
}) {
  const fill = useColor(color);

  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        display: "inline-block",
        width: size,
        height: size / 2,
        marginLeft: "0.12em",
        backgroundColor: fill,
        // Round above, flat below — the *upper* half of a dot, sitting on the baseline.
        // This is the same shape as the mark's fourth dot; the brand book sets it that
        // way up in `MAGIC.` and `BRAND DNA.`, and inverted it stops matching the logo.
        borderRadius: `${size}px ${size}px 0 0`,
        verticalAlign: "baseline",
      }}
    />
  );
}

/**
 * Text followed by the halfdot, with the mark tied to the final word.
 *
 * A line break is permitted immediately before an atomic inline, so a bare
 * `{text}<Halfdot />` drops the accent onto a line of its own the moment the column is
 * a few pixels too narrow. A word joiner (U+2060) does **not** prevent that — measured
 * in both WebKit and Chromium, the break happens either way. Binding the last word and
 * the mark together inside a `nowrap` span does prevent it, and leaves every earlier
 * word free to wrap normally.
 */
export function WithHalfdot({
  children,
  size,
  color,
}: {
  children: string;
  size?: number;
  /** Passed through to {@link Halfdot}. Needed on any surface that is not `surface`:
   *  the default `accentText` is Magic Violet, which vanishes on a violet band. */
  color?: ValidColor;
}) {
  const words = children.trimEnd().split(" ");
  const last = words.pop() ?? "";
  const head = words.join(" ");

  return (
    <>
      {head ? `${head} ` : null}
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {last}
        <Halfdot size={size} color={color} />
      </Box>
    </>
  );
}
